import { useStore } from '@nanostores/react';
import { atom, onMount } from 'nanostores';

/**
 * A `reconnect` acts at once. A `transition` never reuses a cached verdict, since the
 * connection just changed. A `poll` may.
 */
type Signal = 'reconnect' | 'transition' | 'poll';

const PROBE_TIMEOUT_MS = 8_000;
const PROBE_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000];
// Stops a flapping interface probing per signal, which would pin the backoff to its first tier.
const PROBE_MIN_INTERVAL_MS = 2_000;
// Without it, a flag stuck at false re-probes on every tab focus.
const PROBE_FRESH_MS = 30_000;

/**
 * `navigator.onLine` only reports whether an interface is up, and reads false on VPN
 * connect, sleep/wake and some proxies while connectivity is fine. So it is a trigger,
 * never the answer: only a request that fails publishes `false`.
 */
export const $isOnline = atom<boolean>(true);

let probeInFlight = false;
let signalDuringProbe = false;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retryIndex = 0;
let lastProbeAt = 0;
let lastSuccessAt = 0;
let activeProbe: AbortController | undefined;
// Lets the unmount teardown disown a probe still running, so its result cannot publish.
let probeGeneration = 0;

function clearRetryTimer() {
  if (retryTimer != null) {
    clearTimeout(retryTimer);
    retryTimer = undefined;
  }
}

/**
 * Any response counts, whatever the status. `/` because a static asset can be deleted or
 * moved to a CDN. `redirect: 'manual'` because the EU origin answers with a cross-origin
 * 302 that CORS would reject. `AbortController` because older Safari lacks
 * `AbortSignal.timeout()`, and the throw would read as offline.
 */
async function attempt(method: 'OPTIONS' | 'GET'): Promise<boolean> {
  const controller = new AbortController();
  activeProbe = controller;
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    const response = await fetch('/', { method, cache: 'no-store', redirect: 'manual', signal: controller.signal });
    // Nothing reads the app shell that GET returns, so release the download.
    void response.body?.cancel();
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * `OPTIONS` answers in 4 bytes, but anything that blocks it while ordinary traffic works
 * would hold the UI offline with no way back, so `GET` is the fallback. Each attempt gets
 * its own budget so a dropped `OPTIONS` cannot burn the fallback's.
 */
async function isReachable(generation: number): Promise<boolean> {
  if (await attempt('OPTIONS')) return true;
  // Disowned meanwhile; a second request would outlive the subscriber.
  if (generation !== probeGeneration) return false;

  return attempt('GET');
}

async function probe(): Promise<void> {
  clearRetryTimer();
  signalDuringProbe = false;
  lastProbeAt = Date.now();
  probeInFlight = true;
  const generation = ++probeGeneration;

  let reachable = false;
  try {
    reachable = await isReachable(generation);
  } finally {
    // `void probe()` swallows a rejection, and a latched flag would block every evaluate.
    if (generation === probeGeneration) {
      probeInFlight = false;
      activeProbe = undefined;
    }
  }

  if (generation !== probeGeneration) return;

  // A signal landed mid-probe, so this verdict is stale either way. Re-ask through evaluate
  // so the floor still applies.
  if (signalDuringProbe) {
    evaluate('transition');
    return;
  }

  if (reachable) {
    retryIndex = 0;
    lastSuccessAt = Date.now();
    $isOnline.set(true);
    return;
  }

  $isOnline.set(false);
  const delay = PROBE_RETRY_DELAYS_MS[Math.min(retryIndex, PROBE_RETRY_DELAYS_MS.length - 1)];
  retryIndex += 1;
  // The browser often reports no transition back. Jittered so tabs do not re-probe together.
  retryTimer = setTimeout(() => void probe(), delay * (0.75 + Math.random() * 0.5));
}

function noteSignalDuringProbe(signal: Signal, believedOnline: boolean): void {
  // On reconnect nothing has published yet, so believedOnline is already true and the
  // running probe's stale failure would otherwise flash the banner.
  if (signal === 'reconnect' || !believedOnline) {
    signalDuringProbe = true;
  }
}

function hasFreshVerdict(): boolean {
  return $isOnline.get() && Date.now() - lastSuccessAt < PROBE_FRESH_MS;
}

/** Defers rather than drops, since nothing else would revisit it while online. */
function deferProbe(sinceLastProbe: number): void {
  retryTimer ??= setTimeout(() => void probe(), PROBE_MIN_INTERVAL_MS - sinceLastProbe);
}

function evaluate(signal: Signal) {
  const believedOnline = navigator.onLine && $isOnline.get();

  if (probeInFlight) {
    noteSignalDuringProbe(signal, believedOnline);
    return;
  }

  // Only a successful probe clears offline: the flag can read true with nothing reachable.
  if (believedOnline) return;
  if (signal === 'poll' && hasFreshVerdict()) return;

  const sinceLastProbe = Date.now() - lastProbeAt;
  if (signal !== 'reconnect' && sinceLastProbe < PROBE_MIN_INTERVAL_MS) {
    deferProbe(sinceLastProbe);
    return;
  }

  void probe();
}

onMount($isOnline, () => {
  const handleOnline = () => evaluate('reconnect');
  const handleOffline = () => evaluate('transition');
  const handleVisibilityChange = () => {
    if (!document.hidden) evaluate('poll');
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // The listeners attach only on subscribe, so a module-load reading could be stale for good.
  evaluate('poll');

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    clearRetryTimer();
    activeProbe?.abort();
    activeProbe = undefined;
    probeInFlight = false;
    signalDuringProbe = false;
    retryIndex = 0;
    lastProbeAt = 0;
    lastSuccessAt = 0;
    probeGeneration += 1;
  };
});

export function useNetworkStatus(): 'online' | 'offline' {
  const isOnline = useStore($isOnline);

  return isOnline ? 'online' : 'offline';
}
